/**
 * Portal Manta · KbDataLayer
 * Extraído de App.jsx v29.3 (via AST)
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { DADOS } from '../../data/lotes-pr.js';
import { V } from '../../globals.js';
import { M } from '../../tokens/colors.js';

function KbDataLayer(){
  // ══════════════════════════════════════════════
  //  3.7 · Camada de Dados & Agentes
  //  Mostra: fontes primárias (3 JSONs), data layer API, agentes conectados
  // ══════════════════════════════════════════════

  const FONTES=[
    {
      id:'f_063f41',
      arquivo:'lote-3.json (PER+MEF consolidado)',
      lote:'L3',
      projeto_id:'7a8db216',
      tamanho_kb:4303,
      extracted_at:'2026-04-17 16:19',
      origem:'SharePoint · /Manta/Portais/L345_PR/raw/',
      itens_capex:1293,
      obras:432,
      gatilhos:85,
      status:'ativo',
    },
    {
      id:'f_16be34',
      arquivo:'lote-4.json (PER+MEF consolidado)',
      lote:'L4',
      projeto_id:'740d41df',
      tamanho_kb:6302,
      extracted_at:'2026-04-17 15:44',
      origem:'SharePoint · /Manta/Portais/L345_PR/raw/',
      itens_capex:1891,
      obras:529,
      gatilhos:114,
      status:'ativo',
    },
    {
      id:'f_1bd8de',
      arquivo:'lote-5.json (PER+MEF consolidado)',
      lote:'L5',
      projeto_id:'3b0528ea',
      tamanho_kb:3091,
      extracted_at:'2026-04-17 15:38',
      origem:'SharePoint · /Manta/Portais/L345_PR/raw/',
      itens_capex:951,
      obras:228,
      gatilhos:40,
      status:'ativo',
    },
  ];

  // Condensamento inline no portal: 186 KB total (de 13.7 MB brutos)
  const METRICS={
    bruto_mb:(4303+6302+3091)/1024,
    condensado_kb:186.4,
    ratio:((4303+6302+3091)/186.4).toFixed(1),
  };

  // API exposta do data layer (funções que agentes podem chamar)
  const API=[
    {fn:'DADOS[k]', tipo:'lookup', desc:'Acesso direto a um lote por chave (3, 4, 5). Retorna dict completo do lote.'},
    {fn:'DADOS[k].macro', tipo:'lookup', desc:'KPIs macro: tarifa, TIR, WACC, CAPEX, OPEX, extensão, praças, TKM, CR.'},
    {fn:'DADOS[k].tipologias', tipo:'list', desc:'Tipologias de CAPEX (A1..A13, E1..E9, EQ1..EQ2) com qtd, valor, unidade.'},
    {fn:'DADOS[k].obras_per', tipo:'dict', desc:'Obras do PER por categoria, com coords GPS (lat/lon) quando disponíveis.'},
    {fn:'DADOS[k].dados_manta.compara_parametrico.itens', tipo:'list', desc:'Itens do MEF (cód 1.2.x) com preço total PR Vias.'},
    {fn:'DADOS[k].dados_manta.capex_hier', tipo:'tree', desc:'Hierarquia CAPEX nível 1–2 (grandes grupos e sub-grupos).'},
    {fn:'DADOS[k].gatilhos', tipo:'list', desc:'Gatilhos volumétricos (VMDa 2/3/4 faixas) por trecho com coords.'},
    {fn:'DADOS[k].rede_trechos', tipo:'list', desc:'Rede de trechos: rodovia × SNV × segmento × km inicial/final.'},
    {fn:'DADOS[k].fluxo_caixa', tipo:'dict', desc:'3 TIRs (FCFF, FCFE, DDM).'},
    {fn:'DADOS[k].bdi', tipo:'dict', desc:'BDI por tipo (Construção, Restauração, Conservação, OAE).'},
    {fn:'DADOS[k].opex.categorias', tipo:'list', desc:'OPEX desdobrado por rodovia e sistema (agrupamentos do MEF).'},
    {fn:'DADOS[k].pracas', tipo:'list', desc:'Praças de pedágio com código, rodovia, km, município.'},
    {fn:'DADOS[k].kpis_extra', tipo:'dict', desc:'TKM simples/duplicada, ISSQN, verba fiscalização, garantia, seguro.'},
  ];

  // Agentes do portal conectados ao data layer
  const AGENTES=[
    {
      id:'comparativo',
      nome:'Agente Comparativo',
      localizacao:'3.3 Comparativo L3×L4×L5',
      escopo:'Lê DADOS[3], DADOS[4], DADOS[5] e gera matriz L×L×L com KPIs macro, CU por tipologia, ranking qualitativo e narrativa.',
      acesso:['macro','tipologias','bdi','obras_per','capex_hier'],
      status:'ativo',
      modo:'real · cálculo em JS',
    },
    {
      id:'rag',
      nome:'Agente RAG · Busca',
      localizacao:'3.5 Busca RAG',
      escopo:'Indexa os 3 JSONs + KB Curada + Normas. Responde perguntas em linguagem natural recuperando trechos relevantes.',
      acesso:['macro','tipologias','obras_per','compara_parametrico','rede_trechos','gatilhos'],
      status:'mock',
      modo:'mock · quando integrado à Anthropic API, usará embeddings + top-k retrieval',
    },
    {
      id:'extrator',
      nome:'Agente Extrator ANTT',
      localizacao:'2.2 Estudos em Andamento',
      escopo:'Extrai MEF/PER de editais ANTT novos. Usa os 3 lotes PR como few-shot/referência para estruturar outputs no mesmo formato.',
      acesso:['estrutura DADOS completa (usado como template)'],
      status:'mock',
      modo:'mock · fluxo de 5 etapas · quando real, vai consumir PDFs + Claude API',
    },
    {
      id:'simul',
      nome:'Agente Simulador',
      localizacao:'3.4 Simulações Paramétricas',
      escopo:'Calcula cenários paramétricos (ex.: variação de BDI, CU por km, deslocamento de cronograma) usando os 3 lotes como baseline.',
      acesso:['tipologias','bdi','compara_parametrico','cronograma'],
      status:'ativo',
      modo:'real · sliders → recálculo em JS',
    },
    {
      id:'retigrafico',
      nome:'Renderizador Retigráfico',
      localizacao:'Lote → Retigráfico',
      escopo:'Gera visualização linear das rodovias do lote com obras por tipo, OAEs, passarelas, dispositivos.',
      acesso:['obras_per','rodovias','rede_trechos'],
      status:'ativo',
      modo:'real · SVG render',
    },
  ];

  return(<div style={{padding:'14px 17px',overflowY:'auto'}}>
    <div style={{marginBottom:12}}>
      <div style={{fontSize:14,fontWeight:700,color:V.br,display:'flex',alignItems:'center',gap:8}}>
        <span style={{fontSize:10,color:V.g4,fontFamily:'monospace',fontWeight:700}}>3.7</span>
        Camada de Dados &amp; Agentes
      </div>
      <div style={{fontSize:10.5,color:V.g5,marginTop:2}}>Fontes primárias dos 3 lotes PR · API interna do portal · agentes que consomem o data layer</div>
    </div>

    {/* Bloco 1: Métricas do pipeline */}
    <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:14}}>
      {[
        {l:'Fontes primárias',v:'3',s:'JSONs L3, L4, L5',c:V.bl},
        {l:'Tamanho bruto',v:METRICS.bruto_mb.toFixed(1)+' MB',s:'3 arquivos combinados',c:V.br},
        {l:'Inline condensado',v:METRICS.condensado_kb.toFixed(0)+' KB',s:'Carregado no portal',c:V.or},
        {l:'Ratio condensamento',v:METRICS.ratio+'×',s:'Brutos ÷ condensados',c:'#1B7A50'},
      ].map((m,i)=>(<div key={i} style={{background:'#fff',border:`1px solid ${V.g2}`,borderRadius:9,padding:'10px 12px'}}>
        <div style={{fontSize:8.5,fontWeight:700,color:V.g4,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:3}}>{m.l}</div>
        <div style={{fontSize:17,fontWeight:800,color:m.c,lineHeight:1.1,fontFamily:'monospace'}}>{m.v}</div>
        <div style={{fontSize:9,color:V.g5,marginTop:1}}>{m.s}</div>
      </div>))}
    </div>

    {/* Bloco 2: Fontes primárias */}
    <div style={{marginBottom:16}}>
      <div style={{fontSize:11,fontWeight:700,color:V.br,marginBottom:7,display:'flex',alignItems:'center',gap:6}}>
        <span style={{color:V.bl}}>◉</span> Fontes primárias ingeridas
      </div>
      <div style={{background:'#fff',border:`1px solid ${V.g2}`,borderRadius:10,overflow:'hidden'}}>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead><tr style={{background:V.g1}}>
            <th style={{padding:'7px 12px',textAlign:'left',fontSize:9.5,fontWeight:700,color:V.g5}}>Lote</th>
            <th style={{padding:'7px 12px',textAlign:'left',fontSize:9.5,fontWeight:700,color:V.g5}}>Arquivo / origem</th>
            <th style={{padding:'7px 12px',textAlign:'left',fontSize:9.5,fontWeight:700,color:V.g5}}>Projeto ID</th>
            <th style={{padding:'7px 12px',textAlign:'right',fontSize:9.5,fontWeight:700,color:V.g5}}>Tamanho</th>
            <th style={{padding:'7px 12px',textAlign:'right',fontSize:9.5,fontWeight:700,color:V.g5}}>CAPEX itens</th>
            <th style={{padding:'7px 12px',textAlign:'right',fontSize:9.5,fontWeight:700,color:V.g5}}>Obras</th>
            <th style={{padding:'7px 12px',textAlign:'right',fontSize:9.5,fontWeight:700,color:V.g5}}>Gatilhos</th>
            <th style={{padding:'7px 12px',textAlign:'left',fontSize:9.5,fontWeight:700,color:V.g5}}>Extraído em</th>
          </tr></thead>
          <tbody>{FONTES.map((f,i)=>(<tr key={f.id} style={{borderTop:`1px solid ${V.g1}`,background:i%2===0?'#fff':V.g0}}>
            <td style={{padding:'7px 12px',fontSize:11,fontWeight:700,color:V.or,fontFamily:'monospace'}}>{f.lote}</td>
            <td style={{padding:'7px 12px',fontSize:10}}>
              <div style={{color:V.br,fontWeight:600}}>{f.arquivo}</div>
              <div style={{color:V.g4,fontSize:9,marginTop:1,fontFamily:'monospace'}}>{f.origem}</div>
            </td>
            <td style={{padding:'7px 12px',fontSize:9.5,color:V.g5,fontFamily:'monospace'}}>{f.projeto_id}</td>
            <td style={{padding:'7px 12px',textAlign:'right',fontSize:10,color:V.g7,fontFamily:'monospace'}}>{(f.tamanho_kb/1024).toFixed(1)} MB</td>
            <td style={{padding:'7px 12px',textAlign:'right',fontSize:10.5,color:V.br,fontFamily:'monospace',fontWeight:700}}>{f.itens_capex.toLocaleString('pt-BR')}</td>
            <td style={{padding:'7px 12px',textAlign:'right',fontSize:10.5,color:V.br,fontFamily:'monospace',fontWeight:700}}>{f.obras}</td>
            <td style={{padding:'7px 12px',textAlign:'right',fontSize:10.5,color:V.br,fontFamily:'monospace',fontWeight:700}}>{f.gatilhos}</td>
            <td style={{padding:'7px 12px',fontSize:9.5,color:V.g5,fontFamily:'monospace'}}>{f.extracted_at}</td>
          </tr>))}</tbody>
        </table>
      </div>
      <div style={{fontSize:9.5,color:V.g5,marginTop:6,lineHeight:1.55}}>
        Os arquivos originais somam <b>13,7 MB</b>. O portal ingere uma versão <b>condensada</b> (~186 KB) com os campos usados pelos módulos: macro, tipologias, obras com coords, CAPEX hierárquico nível 1–2, OPEX por categoria, praças, BDI, fluxo de caixa, gatilhos e rede. Os dados brutos ficam no SharePoint como fonte de verdade — o portal sincroniza-se via re-ingestão quando os arquivos mudam.
      </div>
    </div>

    {/* Bloco 3: API interna */}
    <div style={{marginBottom:16}}>
      <div style={{fontSize:11,fontWeight:700,color:V.br,marginBottom:7,display:'flex',alignItems:'center',gap:6}}>
        <span style={{color:V.bl}}>⌘</span> API do data layer · chamadas disponíveis aos agentes
      </div>
      <div style={{background:'#fff',border:`1px solid ${V.g2}`,borderRadius:10,overflow:'hidden'}}>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead><tr style={{background:V.g1}}>
            <th style={{padding:'6px 12px',textAlign:'left',fontSize:9.5,fontWeight:700,color:V.g5,width:'38%'}}>Função / path</th>
            <th style={{padding:'6px 12px',textAlign:'left',fontSize:9.5,fontWeight:700,color:V.g5,width:62}}>Tipo</th>
            <th style={{padding:'6px 12px',textAlign:'left',fontSize:9.5,fontWeight:700,color:V.g5}}>Descrição</th>
          </tr></thead>
          <tbody>{API.map((a,i)=>(<tr key={i} style={{borderTop:`1px solid ${V.g1}`,background:i%2===0?'#fff':V.g0}}>
            <td style={{padding:'6px 12px',fontSize:9.5,color:V.bl,fontFamily:'monospace',fontWeight:600}}>{a.fn}</td>
            <td style={{padding:'6px 12px',fontSize:9,color:V.g5}}>
              <span style={{display:'inline-block',padding:'1px 6px',borderRadius:8,background:a.tipo==='list'?V.blb:a.tipo==='dict'?V.orp:a.tipo==='tree'?'#F5F3FF':V.g1,color:a.tipo==='list'?V.bl:a.tipo==='dict'?V.br:a.tipo==='tree'?'#6D28D9':V.g5,fontSize:9,fontWeight:600}}>{a.tipo}</span>
            </td>
            <td style={{padding:'6px 12px',fontSize:10,color:V.g7}}>{a.desc}</td>
          </tr>))}</tbody>
        </table>
      </div>
    </div>

    {/* Bloco 4: Agentes conectados */}
    <div style={{marginBottom:12}}>
      <div style={{fontSize:11,fontWeight:700,color:V.br,marginBottom:7,display:'flex',alignItems:'center',gap:6}}>
        <span style={{color:V.or}}>✦</span> Agentes do portal · escopo e acesso aos dados
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(340px,1fr))',gap:10}}>
        {AGENTES.map(a=>{
          const ativo=a.status==='ativo';
          return(<div key={a.id} style={{background:'#fff',border:`1px solid ${V.g2}`,borderLeft:`4px solid ${ativo?'#1B7A50':V.g4}`,borderRadius:9,padding:'11px 13px'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:4}}>
              <div style={{fontSize:11.5,fontWeight:700,color:V.br}}>{a.nome}</div>
              <span style={{padding:'1px 7px',fontSize:8.5,fontWeight:700,borderRadius:8,letterSpacing:'.04em',background:ativo?'#EBF7F1':V.g1,color:ativo?'#1B7A50':V.g4}}>{ativo?'ATIVO':'MOCK'}</span>
            </div>
            <div style={{fontSize:9,color:V.g4,fontFamily:'monospace',marginBottom:5}}>📍 {a.localizacao}</div>
            <div style={{fontSize:10,color:V.g7,lineHeight:1.5,marginBottom:7}}>{a.escopo}</div>
            <div style={{fontSize:8.5,fontWeight:700,color:V.g4,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:3}}>Acesso ao data layer</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:4,marginBottom:6}}>
              {a.acesso.map((ac,i)=>(<span key={i} style={{padding:'1px 6px',background:V.blb,color:V.bl,fontSize:9,fontWeight:600,borderRadius:10,fontFamily:'monospace'}}>{ac}</span>))}
            </div>
            <div style={{fontSize:8.5,fontWeight:700,color:V.g4,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:2}}>Modo de execução</div>
            <div style={{fontSize:9.5,color:V.g5,fontStyle:'italic'}}>{a.modo}</div>
          </div>);
        })}
      </div>
    </div>

    {/* Bloco 5: Pipeline de atualização */}
    <div style={{background:V.orp,border:`1px solid ${V.orm}`,borderRadius:10,padding:'11px 14px'}}>
      <div style={{fontSize:11,fontWeight:700,color:V.br,marginBottom:7}}>🔄 Pipeline de atualização de dados</div>
      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',fontSize:10}}>
        <span style={{padding:'5px 10px',background:'#fff',borderRadius:6,fontWeight:600,color:V.br,border:`1px solid ${V.orm}`}}>1. Edital ANTT publicado</span>
        <span style={{color:V.or}}>→</span>
        <span style={{padding:'5px 10px',background:'#fff',borderRadius:6,fontWeight:600,color:V.br,border:`1px solid ${V.orm}`}}>2. Extrator roda · lote-X.json gerado</span>
        <span style={{color:V.or}}>→</span>
        <span style={{padding:'5px 10px',background:'#fff',borderRadius:6,fontWeight:600,color:V.br,border:`1px solid ${V.orm}`}}>3. Salva em SharePoint</span>
        <span style={{color:V.or}}>→</span>
        <span style={{padding:'5px 10px',background:'#fff',borderRadius:6,fontWeight:600,color:V.br,border:`1px solid ${V.orm}`}}>4. Portal ingere condensado</span>
        <span style={{color:V.or}}>→</span>
        <span style={{padding:'5px 10px',background:V.or,borderRadius:6,fontWeight:700,color:'#fff'}}>5. Agentes consomem</span>
      </div>
      <div style={{fontSize:9.5,color:V.br2,marginTop:8,lineHeight:1.55}}>
        <b>Estado atual:</b> ingestão manual (JSONs anexados na conversa ou puxados via Microsoft 365 MCP quando conector ativo). <b>Fase 3 do roadmap Manta:</b> cron job no SharePoint detecta mudança de arquivo → re-ingere automaticamente no portal sem intervenção humana.
      </div>
    </div>

  </div>);
}

export { KbDataLayer };
export default KbDataLayer;
